/**
 * Angebots-Detail.
 *
 * Zweispaltig: links Status, Beteiligte, Mail-Verlauf, Gesprächsnotizen und
 * Statusverlauf – rechts der Datensatz und die weiteren Angebote des Kunden.
 *
 * Zwei Warnbanner sind bewusst dauerhaft, nicht flüchtig:
 *  - fehlgeschlagener Abgleich mit den angebundenen Systemen
 *  - auf Wiedervorlage, aber ohne Termin
 */

import { useEffect, useMemo, useState, type ReactNode  } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { Kommentarfaden } from '../komponenten/Kommentarfaden';
import { useKora, useMeta } from '../zustand';
import {
  IconGesendet,
  IconMail,
  IconWindowsOrdner,
  IconRechnung,
  IconStift,
  IconTelefon,
  IconWarnung,
} from '../komponenten/Icons';
import { AuftragModal } from '../modals/AuftragModal';
import { VersendetModal } from '../modals/VersendetModal';
import { GewerkModal } from '../modals/GewerkModal';
import { AnrufModal } from '../modals/AnrufModal';
import { ZustaendigModal } from '../modals/ZustaendigModal';
import { LiegtBei } from '../komponenten/LiegtBei';
import type { LiegtBei as LiegtBeiTyp } from '../typen';
import { AnkuendigungModal } from '../modals/AnkuendigungModal';
import { MailVorschauModal } from '../modals/MailVorschauModal';
import { KundenModal } from '../modals/KundenModal';
import { AbnahmeModal } from '../modals/AbnahmeModal';
import { ZurueckModal } from '../modals/ZurueckModal';
import { MailAnsichtModal } from '../modals/MailAnsichtModal';
import { dat, istMobil, whatsappNummer } from '../hilfen/format';
import { Betragsfeld } from '../komponenten/Betragsfeld';
import { chronikBauen } from '../hilfen/chronik';
import { Pruefblock } from '../komponenten/Pruefblock';
import { Modal, KnopfPrimaer, KnopfSekundaer } from '../komponenten/Modal';
import { Versandschritte } from '../komponenten/Versandschritte';
import { Abwicklungsblock } from '../komponenten/Abwicklungsblock';
import { Pruefbericht } from '../komponenten/Pruefbericht';
import type { Angebot, PruefAntwort, AngebotAntwort, Erinnerung } from '../typen';
import './detail.css';


/**
 * Der Weg in den Vorgangsordner (v14, 17.7).
 *
 * `explorerLink` ruft über `kora://` das Hilfsprogramm auf und öffnet den
 * Ordner im Windows-Explorer. Das Hilfsprogramm liegt im Profil zum
 * Herunterladen; ohne es tut der Link lautlos nichts, deshalb zeigt der
 * Klick zusätzlich den Pfad als Hinweis.
 *
 * Der zweite Weg – derselbe Ordner im Browser – ist am 24.09.2026
 * entfallen. Dateien liegen im Vorgangsordner, und den gibt es genau
 * einmal; zwei Symbole für dasselbe Ziel haben nur die Frage erzeugt,
 * welches denn nun das richtige ist.
 */
/**
 * Der Autor, unter dem der Altbestand eingelesen wurde.
 *
 * Steht so in der Chronik und bleibt dort stehen (v14, 17.8): Historische
 * Einträge sagen wahrheitsgemäß, woher sie stammen. Die Rücknahme des
 * letzten Statuswechsels gilt nur für Wechsel aus Kora – einen
 * übernommenen Eintrag nimmt man nicht zurück.
 */
const BESTANDSQUELLE = 'Monday';

function explorerLink(status: string, ordnerName: string): string {
  const art = status === 'Auftrag erteilt' || status === 'Abgenommen' ? 'auftrag' : 'angebot';
  return `kora://oeffne?art=${art}&ordner=${encodeURIComponent(ordnerName)}`;
}

/**
 * Auswahl, in welches Gewerk gesprungen wird.
 *
 * Bei einem einzelnen Gewerk ist ein Link richtig; laufen mehrere unter einer
 * Nummer, wäre ein Link eine Entscheidung, die Kora nicht treffen kann. Das
 * eigene Gewerk steht oben und ist markiert – es ist der häufigste Fall.
 */
function Quellenwahl({
  titel,
  symbol,
  eintraege,
  onKlick,
}: {
  titel: string;
  symbol: ReactNode;
  /** Wird beim Anklicken eines Eintrags ausgelöst – für den Hinweis, falls nichts passiert. */
  onKlick?: () => void;
  eintraege: {
    schluessel: string;
    label: string;
    eigenes: boolean;
    ziel: string;
    extern: boolean;
    hinweis?: string;
  }[];
}) {
  const [offen, setOffen] = useState(false);
  if (eintraege.length === 0) return null;

  const sortiert = [...eintraege].sort((x, y) => Number(y.eigenes) - Number(x.eigenes));

  return (
    <span className="detail-quellenwahl">
      <button
        type="button"
        className="detail-quelle"
        aria-expanded={offen}
        onClick={() => setOffen((o) => !o)}
      >
        {symbol}
        {titel}
        <span className="detail-quelle-pfeil">{offen ? '▴' : '▾'}</span>
      </button>

      {offen && (
        <span className="detail-quellenmenue">
          {sortiert.map((e) => (
            <a
              key={e.schluessel}
              href={e.ziel}
              target={e.extern ? '_blank' : undefined}
              rel={e.extern ? 'noreferrer' : undefined}
              title={e.hinweis}
              onClick={() => {
                setOffen(false);
                onKlick?.();
              }}
            >
              <span className="detail-quellenmenue-label">
                {e.label}
                {e.eigenes && <span className="detail-quellenmenue-marke">geöffnet</span>}
              </span>
              {e.hinweis && <span className="detail-quellenmenue-pfad">{e.hinweis}</span>}
            </a>
          ))}
        </span>
      )}
    </span>
  );
}

/**
 * Wohin der Zurück-Link führt, je nachdem woher man kam.
 *
 * Der Schlüssel reist im Routing-Zustand mit. Wer hier eine Herkunft ergänzt,
 * muss sie auch an der aufrufenden Stelle mitgeben – sonst greift der Fallback.
 */
/**
 * Braucht dieser Status eine abgeschlossene Prüfung?
 *
 * Dieselben drei wie auf dem Server. Ab „Gesendet" ist das Angebot beim Kunden –
 * ab da lässt sich ein Fehler nicht mehr still korrigieren.
 */
function pruefpflichtig(status: string): boolean {
  return status === 'Gesendet' || status === 'Auftrag erteilt' || status === 'Abgenommen';
}
const HERKUENFTE: Record<string, { key: string; pfad: string; name: string }> = {
  cockpit: { key: 'cockpit', pfad: '/cockpit', name: 'Angebote' },
  kunden: { key: 'kunden', pfad: '/kunden', name: 'Kundenakte' },
  meintag: { key: 'meintag', pfad: '/', name: 'Mein Tag' },
  auftraege: { key: 'auftraege', pfad: '/auftraege', name: 'Auftragsauswertung' },
  abwicklung: { key: 'abwicklung', pfad: '/abwicklung', name: 'Aufträge' },
};

export function AngebotDetail() {
  const { id } = useParams();
  const angebotId = Number(id);
  const navigate = useNavigate();
  const ort = useLocation();
  const queryClient = useQueryClient();
  const { toastZeigen, nutzer } = useKora();
  const meta = useMeta();

  /** Aufgeklappte Löschabfrage. Zweistufig, weil der Schritt endgültig ist. */
  const [loeschAbfrage, setLoeschAbfrage] = useState(false);
  const [auftragOffen, setAuftragOffen] = useState(false);
  const [versendetOffen, setVersendetOffen] = useState(false);
  const [gewerkOffen, setGewerkOffen] = useState(false);
  const [anrufOffen, setAnrufOffen] = useState(false);
  /* Übergabe des aktuellen Schritts an jemand anderen (Mein Tag v6, Richtlinie 21). */
  const [uebergabe, setUebergabe] = useState<LiegtBeiTyp | null>(null);
  const [ankuendigungOffen, setAnkuendigungOffen] = useState(false);
  /** „Gesendet" gewählt, aber Prüfung oder Ankündigung fehlen: Warnung statt Wechsel. */
  const [versandWarnung, setVersandWarnung] = useState(false);
  const [mailOffen, setMailOffen] = useState(false);
  const [kalkWechsel, setKalkWechsel] = useState(false);
  const [kundeOffen, setKundeOffen] = useState(false);
  /* Vorgabe zu: Der Platz gehört dem Kommentarfaden (TEAM-CHAT.md, 3.1). */
  /* Kundenkachel zugeklappt – der Platz gehört dem Kommentarfaden (3.1). */
  const [kundeAufgeklappt, setKundeAufgeklappt] = useState(false);
  const [abnahmeOffen, setAbnahmeOffen] = useState(false);
  const [zurueckOffen, setZurueckOffen] = useState(false);
  /** Aufgeklappte Korrektur des Versanddatums. */
  const [versandKorrektur, setVersandKorrektur] = useState(false);
  /** Versendete Mail, die gerade im Wortlaut angesehen wird. */
  const [mailAnsicht, setMailAnsicht] = useState<Erinnerung | null>(null);
  const [mehrOffen, setMehrOffen] = useState(false);
  /** Die Chronik startet zu – sichtbar bleibt nur die letzte Aktivität. */
  const [chronikOffen, setChronikOffen] = useState(false);
  const [chronikFilter, setChronikFilter] = useState<
    'alle' | 'status' | 'aenderung' | 'mail' | 'rueckmeldung' | 'pruefung'
  >('alle');

  const abfrage = useQuery({
    queryKey: ['angebot', angebotId],
    queryFn: () => api.hole<Angebot>(`/angebote/${angebotId}`),
    enabled: Number.isFinite(angebotId),
  });

  /*
   * Mit Auftrag hergekommen.
   *
   * Die Knöpfe auf „Meine Aufgaben" ändern nichts, sie öffnen etwas:
   * „Ankündigen" und „Anrufen" den Dialog „Ankündigung erfassen", „Versenden"
   * den Versand-Dialog, „Prüfen" den Prüfblock – der rollt selbst in den
   * Blick. Der Auftrag reist als Routing-Zustand mit und wird nach dem Öffnen
   * gelöscht, sonst spränge der Dialog bei jedem Zurück und Vor wieder auf.
   */
  const oeffne = (ort.state as { oeffne?: string } | null)?.oeffne ?? null;
  useEffect(() => {
    if (!oeffne || !abfrage.data) return;
    if (oeffne === 'ankuendigung') setAnkuendigungOffen(true);
    if (oeffne === 'versenden') setVersendetOffen(true);
    /* „Anrufen" aus der Rubrik Nachfassen: die Rückmeldung erfassen. */
    if (oeffne === 'rueckmeldung') setAnrufOffen(true);
    /* Bestellung und Service: der Block rollt in den Blick, quittiert wird dort. */
    if (oeffne === 'abwicklung' || oeffne === 'service') {
      window.setTimeout(() => {
        document.getElementById(oeffne)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    }
    const herkunft = (ort.state as { herkunft?: string } | null)?.herkunft;
    navigate(ort.pathname, { replace: true, state: herkunft ? { herkunft } : null });
  }, [oeffne, abfrage.data, navigate, ort.pathname, ort.state]);

  const alleAbfrage = useQuery({
    queryKey: ['angebote'],
    queryFn: () => api.hole<Angebot[]>('/angebote'),
  });

  function aktualisieren() {
    void queryClient.invalidateQueries({ queryKey: ['angebot', angebotId] });
    void queryClient.invalidateQueries({ queryKey: ['angebote'] });
  }

  /**
   * Zuständigkeit wechseln – der Vorgang liegt danach bei der neuen Person.
   */
  const kalkulantMutation = useMutation({
    mutationFn: (kalkulant: string) =>
      api.aendere<Angebot>(`/angebote/${angebotId}/kalkulant`, { kalkulant }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
      setKalkWechsel(false);
      toastZeigen('Zuständigkeit gewechselt.');
    },
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Wechsel fehlgeschlagen.'),
  });

  const statusMutation = useMutation({
    mutationFn: (status: string) =>
      api.sende<AngebotAntwort>(`/angebote/${angebotId}/status`, { status }),
    onSuccess: (a) => {
      aktualisieren();
      if (a.sync.fehler) toastZeigen('Status gespeichert – der Abgleich meldet ein Problem.');
    },
  });

  /*
   * Den letzten Statuswechsel zurücknehmen – nur für Verklicker.
   *
   * Versteckt im ⋯-Menü und nur am selben Tag: Wer sich vergriffen hat, soll
   * es finden; wer es nicht braucht, soll sich nicht eingeladen fühlen. Der
   * Server prüft die Frist noch einmal.
   */
  const [zurueckOffen2, setZurueckOffen2] = useState(false);
  const statusZurueck = useMutation({
    mutationFn: () => api.sende<AngebotAntwort>(`/angebote/${angebotId}/status-zurueck`),
    onSuccess: (a) => {
      aktualisieren();
      setZurueckOffen2(false);
      toastZeigen(a.toast ?? 'Zurückgenommen.');
    },
    onError: (f) => {
      setZurueckOffen2(false);
      toastZeigen(f instanceof Error ? f.message : 'Zurücknehmen fehlgeschlagen.');
    },
  });

  /**
   * Löscht den Vorgang in Kora.
   *
   * Nur für Fehlgriffe und Testanlagen, deshalb der Administration
   * vorbehalten. Kora räumt ausschließlich die eigenen Daten weg; der
   * Vorgangsordner bleibt stehen und wird in der Rueckmeldung benannt. Anschließend zurück zur Liste – die Seite hätte keinen
   * Datensatz mehr zum Anzeigen.
   */
  const loeschMutation = useMutation({
    mutationFn: () =>
      api.loesche<{ nr: number; nummerFrei: boolean; offen: string[] }>(
        `/angebote/${angebotId}`,
      ),
    onSuccess: (a) => {
      const teile = [`Angebot ${a.nr} gelöscht`];
      if (a.nummerFrei) teile.push('die Nummer ist wieder frei');
      if (a.offen.length) teile.push(`nicht entfernt: ${a.offen.join(', ')}`);
      toastZeigen(teile.join(' - '));
      void queryClient.invalidateQueries();
      navigate('/cockpit');
    },
    onError: (f: Error) => toastZeigen(f.message),
  });

  /**
   * Die Ablage des Vorgangs neu suchen.
   *
   * Für den Fall, dass der Ordner umbenannt oder unter derselben Nummer neu
   * angelegt wurde. Das Ergebnis wird ausführlich gemeldet und nicht nur als
   * „erledigt" quittiert: Findet die Suche nichts oder zu viel, muss der
   * Benutzer wissen, woran er ist.
   */
  const verknuepfungMutation = useMutation({
    mutationFn: () =>
      api.sende<{
        gesucht: string;
        ablage: { stand: string; text: string };
        geaendert: boolean;
      }>(`/angebote/${angebotId}/verknuepfung`),
    onSuccess: (r) => {
      aktualisieren();
      toastZeigen(r.ablage.text);
    },
    onError: (f: Error) => toastZeigen(f.message),
  });

  /**
   * Versanddatum richtigstellen.
   *
   * Das Datum kommt aus dem übernommenen Protokoll und ist zu jung, wenn der
   * Status nachträglich gesetzt wurde. Ohne diese Korrektur bliebe die
   * Liegezeit dauerhaft falsch – und sie ist die Zahl, nach der gearbeitet wird.
   */
  const versanddatumMutation = useMutation({
    mutationFn: (datum: string) =>
      api.aendere<{ toast?: string }>(`/angebote/${angebotId}/versanddatum`, { datum }),
    onSuccess: (r) => {
      aktualisieren();
      setVersandKorrektur(false);
      if (r.toast) toastZeigen(r.toast);
    },
    onError: (f: Error) => toastZeigen(f.message),
  });

  const syncMutation = useMutation({
    mutationFn: () => api.sende<AngebotAntwort>(`/angebote/${angebotId}/sync`),
    onSuccess: (a) => {
      aktualisieren();
      if (a.toast) toastZeigen(a.toast);
    },
  });

  const wertMutation = useMutation({
    mutationFn: (auftrag: number | null) =>
      api.aendere<Angebot>(`/angebote/${angebotId}/auftragswert`, { auftrag }),
    onSuccess: aktualisieren,
  });



  /*
   * Die Prüfungen kommen aus derselben Abfrage wie der Prüfblock – React Query
   * liefert sie aus dem Zwischenspeicher, es ist keine zweite Anfrage.
   *
   * Muss vor jedem frühen Return stehen: Ein Hook hinter einer Bedingung ändert
   * die Reihenfolge zwischen Ladezustand und fertiger Ansicht, und React bricht
   * mit „Rendered more hooks than during the previous render" ab.
   */
  const pruefAbfrage = useQuery({
    queryKey: ['pruefung', Number(angebotId)],
    queryFn: () => api.hole<PruefAntwort>(`/pruefung/angebot/${angebotId}/pruefungen`),
    enabled: !!angebotId,
  });

  const a = abfrage.data;

  const geschwister = useMemo(
    () => (alleAbfrage.data ?? []).filter((x) => a && x.kunde.id === a.kunde.id && x.id !== a.id),
    [alleAbfrage.data, a],
  );

  if (abfrage.isLoading) {
    return <div className="eh-inhalt">Angebot wird geladen …</div>;
  }
  if (!a) {
    return (
      <div className="eh-inhalt">
        <div className="eh-karte">Dieses Angebot gibt es nicht (mehr).</div>
      </div>
    );
  }

  /**
   * Der Fortschritt als Zeitleiste – nur Anzeige, nichts zum Klicken.
   *
   * Gesetzt wird oben über „Status ändern". Hier steht, wo der Vorgang steht
   * und wie er dahin kam: je Station, wer sie gesetzt hat und wann. Der frühere
   * Erklärtext ist entfallen – er sagte, warum etwas noch nicht geht, und das
   * beantwortet sich, sobald die Stationen leer oder gefüllt sind.
   *
   * „Rückmeldung erfassen" steht bewusst nicht hier: Ein Kundenanruf ist kein
   * Status, den jemand gesetzt hat, sondern ein Ereignis.
   */
  /*
   * Die vier Abwicklungsstationen stehen als kleinere Zwischenschritte
   * zwischen „Auftrag erteilt" und „Abgenommen". Sie sind Stati wie die
   * anderen, nur eben Etappen einer Phase, nicht Phasen. Ein Status, den die Auswahl
   * kennt und die Zeitleiste verschweigt, wäre ein Widerspruch.
   */
  /*
   * Die laufende Phase bekommt den Platz. In der Angebotsphase sind die vier
   * Abwicklungsstationen nur Punkte ohne Beschriftung – man sieht, dass da
   * noch etwas kommt, ohne dass es die Achse zerdrückt. Im Auftrag werden die
   * Angebotsstationen klein: Sie sind Geschichte, die Abwicklung ist jetzt.
   */
  /*
   * Sechs Knoten, nicht dreizehn (Richtlinie 1): Die zehn Abwicklungsschritte
   * kommen nicht in die Hauptlinie – sie würden suggerieren, der Auftrag habe
   * einen einzigen Stand, obwohl jede Bestellung ihren eigenen hat. Zwischen
   * „Auftrag erteilt" und „Abgenommen" zeigt ein gestricheltes Segment, dass
   * dort ein Teilprozess läuft und wo jede Bestellung steht.
   *
   * Der Kopf sagt in der Abwicklung „Auftrag erteilt", auch wenn der
   * abgeleitete Status „Bestellt" oder „In Produktion" heißt: Der
   * Abwicklungsstand folgt aus den Bestellungen, die Pille daneben nennt ihn.
   */
  const auftragsphase = meta.auftragsStatus.includes(a.status);
  const anzeigeStatus = auftragsphase && a.status !== 'Abgenommen' ? 'Auftrag erteilt' : a.status;
  const stationen: { status: string }[] = [
    'START',
    'in Arbeit',
    'Freigabe Technik',
    'Gesendet',
    'Auftrag erteilt',
    'Abgenommen',
  ].map((status) => ({ status }));
  const aktuellIndex = stationen.findIndex((st) => st.status === anzeigeStatus);

  const erreicht = new Map<string, { user: string; datumText: string }>();
  for (const v of a.verlauf) {
    // Bei mehrfach durchlaufenen Stationen zählt der letzte Durchgang.
    erreicht.set(v.status, { user: v.user, datumText: v.datumText });
  }
  /*
   * START ist bei jedem Vorgang durchlaufen – ohne Anlage gäbe es ihn nicht.
   * Für in Kora angelegte Angebote steht der Eintrag im Verlauf; für die
   * übernommenen fehlt er, dort bleibt wenigstens das Anlagedatum.
   */
  if (!erreicht.has('START')) {
    erreicht.set('START', { user: '', datumText: dat(a.angelegtAm) });
  }

  /**
   * Wohin führt ein gewählter Status?
   *
   * Manche Wechsel brauchen Angaben – ein Versanddatum, eine Auftragshöhe,
   * eine Begründung. Statt sie stumm zu setzen, öffnet die Auswahl den
   * passenden Dialog. So gibt es einen Ort für den Statuswechsel, ohne dass
   * Pflichtangaben verlorengehen.
   */
  function statusWaehlen(ziel: string) {
    if (!ziel) return;
    if (ziel === 'Gesendet') {
      /*
       * Wählbar, aber nicht ohne Vorarbeit: Fehlt Prüfung oder Ankündigung,
       * kommt eine Warnung und der Status bleibt, wie er ist. Ein gesperrter
       * Eintrag sagte nur „geht nicht" – die Warnung sagt, was zu tun ist.
       */
      if (a && (a.versand.pruefungOffen || a.versand.ankuendigungOffen)) {
        setVersandWarnung(true);
        return;
      }
      setVersendetOffen(true);
      return;
    }
    if (ziel === 'Auftrag erteilt') { setAuftragOffen(true); return; }
    if (ziel === 'Wiedervorlage' || ziel === 'Abgesagt') { setAnrufOffen(true); return; }
    /*
     * Zurück auf START heißt, dass das Angebot überarbeitet wird. Der
     * Kalkulant muss den Grund erfahren – den kennt nur, wer mit dem Kunden
     * gesprochen hat. Deshalb ein eigener Dialog statt eines stummen Wechsels.
     */
    if (ziel === 'START') { setZurueckOffen(true); return; }
    statusMutation.mutate(ziel);
  }

  /**
   * Chronik: Statuswechsel, Mails und Rückmeldungen in einem Strom.
   *
   * Vorher standen sie in drei getrennten Karten untereinander und machten die
   * Seite lang, obwohl fast immer nur der letzte Eintrag interessiert. Jetzt
   * bleibt sichtbar, was zuletzt geschah; alles Übrige liegt eine Klick tief.
   */
  const chronik = chronikBauen(a, pruefAbfrage.data?.pruefungen ?? []);

  /**
   * Öffnet der Klick nichts, sagt es Kora.
   *
   * `kora://` wird von Windows ignoriert, wenn das Hilfsprogramm fehlt – ohne
   * Fehlermeldung, ohne irgendein Zeichen. Startet es dagegen, verliert das
   * Fenster den Fokus. Genau daran lässt es sich erkennen: Ist der Fokus nach
   * anderthalb Sekunden noch hier, ist nichts passiert.
   */
  function projektHinweis() {
    let gestartet = false;
    const merken = () => {
      gestartet = true;
    };
    window.addEventListener('blur', merken, { once: true });
    window.setTimeout(() => {
      window.removeEventListener('blur', merken);
      if (!gestartet) {
        toastZeigen(
          'Es hat sich nichts geöffnet? Dafür braucht dieser Rechner einmal die Einrichtung – ' +
            'sie liegt im Profil unter „Ordner im Explorer öffnen".',
        );
      }
    }, 1500);
  }

  /** Zähler je Filter – sie stehen im Chip, damit niemand blind hineinklickt. */
  const chronikZahl = {
    alle: chronik.length,
    status: chronik.filter((c) => c.art === 'status').length,
    aenderung: chronik.filter((c) => c.art === 'aenderung').length,
    mail: chronik.filter((c) => c.art === 'mail').length,
    pruefung: chronik.filter((c) => c.art === 'pruefung').length,
    rueckmeldung: chronik.filter((c) => c.art === 'rueckmeldung').length,
  };
  const letzteAktivitaet = chronik[0] ?? null;
  const chronikGefiltert = chronikFilter === 'alle' ? chronik : chronik.filter((c) => c.art === chronikFilter);

  const versendet = a.versandHistorie.length > 0;

  /**
   * Der Vorgang ist ein Auftrag, kein Angebot mehr.
   *
   * Damit endet die Angebotslogik: Es wird nicht mehr nachgefasst, keine
   * Rückmeldung erfasst, keine Erinnerungsmail geschrieben. Übrig bleibt der
   * eine Schritt, der noch aussteht – die Abnahme.
   *
   * Verlauf und Chronik bleiben vollständig sichtbar: Der Lebenszyklus hört
   * nicht auf nachvollziehbar zu sein, nur weil die Phase gewechselt hat.
   */
  /*
   * „Auftrag" umfasst jetzt auch die Abwicklung: Bestellung, AB, Produktion.
   * Alles, was hier ausgeblendet wird – Nachfassen, Erinnerung, Prüfblock –
   * gehört zur Angebotsphase und hat auch dort nichts mehr verloren.
   */
  const istAuftrag = meta.auftragsStatus.includes(a.status);
  /** Läuft noch, ist aber schon beauftragt – dort gibt es die Abwicklungsschritte. */
  const inAbwicklung = istAuftrag && a.status !== 'Abgenommen';

  /**
   * Stand des Nachfassens als eine Zeile.
   *
   * „pausiert" stand hier vorher allein und sagte nichts: Der häufigste Grund
   * ist, dass das Angebot schlicht noch nicht beim Kunden liegt – dann gibt es
   * nichts nachzufassen. Jede Zeile nennt jetzt den Grund.
   *
   * Reihenfolge ist Absicht: Zuerst die Fälle, die das Nachfassen ganz
   * ausschließen, dann die, die es aufschieben, zuletzt der Normalfall.
   */
  const nachfassText = a.nachfass.antwortAm
    ? `Kunde hat am ${dat(a.nachfass.antwortAm)} geantwortet`
    : !versendet
      ? 'startet mit dem Versand'
      : !meta.aktiveStatus.includes(a.status)
        ? `entfällt – Status ${a.status}`
        : a.wvZustand
          ? a.wvText
          : a.nachfass.mailVerfallen
            ? `Anruf dran seit ${dat(a.nachfass.anrufAb)}`
            : a.nachfass.erledigt < 1
              ? `1. Erinnerung am ${dat(a.nachfass.mailAm)}`
              : a.nachfass.anrufFaellig
                ? `Anruf ausstehend seit ${dat(a.nachfass.anrufAb)}`
                : `Anruf fällig am ${dat(a.nachfass.anrufAb)}`;

  const nachfassTon = a.nachfass.antwortAm
    ? 'gruen'
    : a.wvZustand === 'ueberfaellig' || a.nachfass.anrufFaellig
      ? 'rot'
      : a.wvZustand === 'faellig'
        ? 'gelb'
        : 'neutral';


  /*
   * Woher kam der Aufruf?
   *
   * Wer aus der Kundenakte kommt, will dorthin zurück, nicht ins Cockpit. Die
   * Herkunft reist als Routing-Zustand mit; fehlt sie – etwa weil jemand den
   * Link direkt aufgerufen oder ein Lesezeichen benutzt hat –, ist das
   * Angebotsübersicht die vernünftigste Annahme.
   */
  const herkunft = HERKUENFTE[(ort.state as { herkunft?: string } | null)?.herkunft ?? ''] ?? HERKUENFTE.cockpit!;

  /*
   * Die Stati der aktuellen Phase, in der Reihenfolge des Ablaufs.
   *
   * Nicht alle: Wer ein Angebot kalkuliert, braucht „AB retour" nicht zu
   * sehen – die Abwicklung beginnt erst mit dem Auftrag. Und wer eine
   * Bestellung verfolgt, braucht „Freigabe Technik" nicht mehr. Gezeigt wird,
   * was in dieser Phase noch kommen kann; wählbar, was der Server als
   * nächsten Schritt nennt. Zusätzlich jedes Ziel, das der Server außerhalb
   * der Phase erlaubt – etwa der Weg zurück, wenn ein Auftrag platzt.
   */
  const angebotsPhase = ['START', 'in Arbeit', 'Freigabe Technik', 'Gesendet', 'Auftrag erteilt', 'Wiedervorlage', 'Abgesagt'];
  const auftragsPhase = ['Auftrag erteilt', ...meta.abwicklungStatus, 'Abgenommen', 'Abgesagt'];
  const phase = (istAuftrag ? auftragsPhase : angebotsPhase).filter((x) => meta.status.includes(x));
  const statusReihe = [...phase, ...a.statusZiele.filter((z) => !phase.includes(z))];

  return (
    <div className="detail">
      {/*
        Das Ziel richtet sich nach der Herkunft – wer aus der Kundenakte kam,
        will dorthin zurück. Die Beschriftung bleibt trotzdem immer „Zurück":
        Wohin es geht, ist im Moment des Klickens ohnehin klar, und wechselnde
        Beschriftungen machen eine Leiste unruhig. Das Ziel steht im Tooltip.
      */}
      <button
        type="button"
        className="detail-zurueck"
        title={`Zurück zu ${herkunft.name}`}
        onClick={() => navigate(herkunft.pfad)}
      >
        ← Zurück
      </button>

      <div className="detail-kopf">
        <div className="detail-kategorie">
          <span className="detail-prefix">{a.prefix}</span>
          <span className="detail-kategorie-text">
            {a.kategorie} · {a.kunde.ort} · angelegt {dat(a.angelegtAm)}
          </span>
        </div>

        {/*
          Titel und Verknüpfungen in einer Zeile (TEAM-CHAT.md, 3.1).
          Beschriftete Links kosteten drei Zeilen Höhe für etwas, das jeder
          nach einer Woche am Symbol erkennt – die Höhe braucht jetzt der
          Kommentarfaden. Der Zweck steht im `title`.
        */}
        <div className="detail-titelzeile">
          <h1 className="detail-name">{a.name}</h1>
          <div className="detail-quellen">
          {a.sammelvorgang ? (
            /*
              Läuft mehr als ein Gewerk unter der Nummer, muss man wählen
              können, wohin man springt. Fenster, Beschattung und
              Insektenschutz teilen sich bei älteren Vorgängen einen Ordner,
              bei neuen nicht mehr. Genau das bildet die Auswahl ab.
            */
            <>
              <Quellenwahl
                titel="Ordner öffnen"
                symbol={<IconWindowsOrdner groesse={16} />}
                onKlick={projektHinweis}
                eintraege={a.geschwisterGewerke.map((g) => ({
                  schluessel: g.prefix,
                  label:
                    `${g.prefix} · ${g.kategorie}` +
                    (g.gleicherOrdner && !g.eigenes ? ' · gemeinsamer Ordner' : ''),
                  eigenes: g.eigenes,
                  ziel: explorerLink(a.status, g.ordnerName),
                  extern: false,
                  hinweis: g.ordnerName,
                }))}
              />
            </>
          ) : (
            <>
              {/*
                Ein Weg zu den Dateien, nicht drei (v14, 17.7): der
                Vorgangsordner auf dem PC. Die beiden anderen Absprünge sind
                am 24.09.2026 entfallen – sie führten zu denselben Dateien
                und ließen jedes Mal offen, welches der Symbole nun das
                richtige ist.
              */}
              <a
                className="detail-quelle"
                href={explorerLink(a.status, a.ordnerName)}
                title={`Öffnet ${a.ordnerName} im Windows-Explorer`}
                onClick={projektHinweis}
              >
                <IconWindowsOrdner groesse={18} />
              </a>
              {/*
                Absprung in die Buchhaltung.
                
                Aus einem Kundenangebot wird irgendwann eine Rechnung, und die
                entsteht in sevDesk. Der Weg dorthin führte bisher über Suchen
                und Abtippen – hier steht der Kontakt, der gemeint ist.
              */}
              {a.kunde.sevdeskUrl && (
                <a
                  className="detail-quelle"
                  href={a.kunde.sevdeskUrl}
                  target="_blank"
                  rel="noreferrer"
                  title={
                    a.kunde.sevdeskNummer
                      ? `Kunde ${a.kunde.sevdeskNummer} in sevDesk öffnen – dort die Rechnung anlegen`
                      : 'Kunden in sevDesk öffnen – dort die Rechnung anlegen'
                  }
                >
                  <IconRechnung groesse={17} />
                </a>
              )}
            </>
          )}
          </div>
        </div>

        {/*
          Die Zuständigkeit steht in der Kopfzeile, nicht in der rechten
          Spalte (TEAM-CHAT.md, 3.1). Das ist die Information, die man beim
          Öffnen zuerst braucht – wer schuldet hier etwas. Vorher lag sie
          drei Bildschirmhöhen entfernt vom Ort der Entscheidung.

          Die Rollen heißen genau „Kalkulant" und „Versender". Wofür eine
          Rolle zuständig ist, steht in der Zuständigkeitsmatrix und nicht
          in der Kopfzeile.
        */}
        <div className="detail-zustaendig" id="kalkulant">
          <span className={`detail-rollechip ${a.kalkulant ? '' : 'fehlt'}`}>
            <span className="detail-rollechip-avatar">{a.kalkInitialen || '?'}</span>
            <span className="detail-rollechip-name">{a.kalkulant || 'Kein Kalkulant'}</span>
            <span className="detail-rollechip-rolle">Kalkulant</span>
          </span>

          {a.versender && (
            <span className="detail-rollechip">
              <span className="detail-rollechip-avatar">{a.versInitialen}</span>
              <span className="detail-rollechip-name">{a.versender}</span>
              <span className="detail-rollechip-rolle">Versender</span>
            </span>
          )}

          {kalkWechsel ? (
            <select
              className="detail-kalk-auswahl"
              value={a.kalkulant}
              disabled={kalkulantMutation.isPending}
              autoFocus
              onChange={(e) => {
                kalkulantMutation.mutate(e.target.value);
                setKalkWechsel(false);
              }}
              onBlur={() => setKalkWechsel(false)}
            >
              {!a.kalkulant && <option value="">Person wählen …</option>}
              {meta.team
                .filter((t) => t.imVertrieb)
                .map((t) => (
                  <option key={t.name} value={t.name}>
                    {t.name}
                  </option>
                ))}
            </select>
          ) : (
            <button
              type="button"
              className="detail-wechseln"
              title="Kalkulant wechseln"
              onClick={() => setKalkWechsel(true)}
            >
              {a.kalkulant ? 'wechseln' : 'wählen'}
            </button>
          )}
        </div>
      </div>

      {a.syncFehler && (
        <div className="detail-warnung">
          <IconWarnung groesse={16} />
          <div className="detail-warnung-text">
            <div className="detail-warnung-titel">
              Status gesetzt, der Abgleich der Ablage ist fehlgeschlagen
            </div>
            <div className="detail-warnung-unter">{a.syncFehler}</div>
          </div>
          <button
            type="button"
            className="detail-warnung-knopf"
            disabled={syncMutation.isPending}
            onClick={() => syncMutation.mutate()}
          >
            {syncMutation.isPending ? 'Läuft …' : 'Erneut versuchen'}
          </button>
        </div>
      )}

      {a.wvKonflikt && (
        <div className="detail-warnung">
          <IconWarnung groesse={16} />
          <div className="detail-warnung-text">
            <div className="detail-warnung-titel">Auf Wiedervorlage, aber ohne Termin</div>
            <div className="detail-warnung-unter">
              Ohne Wiedervorlage-Datum taucht der Vorgang nie wieder auf. Bitte über
              „Rückmeldung erfassen" einen Termin hinterlegen.
            </div>
          </div>
        </div>
      )}

      <div className="detail-raster">
        <div className="detail-spalte">
          <div className="detail-karte detail-statuskarte">
            {/* ---- Statuszeile: der Status genau einmal ---- */}
            <div className="detail-statuszeile">
              <span className="detail-statuspunkt" style={{ background: a.statusPunkt }} />
              <span className="detail-statusname">{anzeigeStatus}</span>
              {istAuftrag ? (
                <span
                  className="detail-chip detail-chip--abwicklung"
                  title="Der Abwicklungsstand ergibt sich aus den Bestellungen – quittiert wird auf der Bestellkarte"
                >
                  {a.abwicklungStand}
                </span>
              ) : (
                <span className="detail-chip">{a.alterText} im Status</span>
              )}
              {/* Service hängt am Auftrag, ändert den Prozessstand nicht (Richtlinie 23). */}
              {a.serviceOffen > 0 && (
                <span className="detail-chip detail-chip--service" title={'Offene Servicepunkte – im Block „Service“ unter den Bestellungen'}>
                  Service offen · {a.serviceOffen} {a.serviceOffen === 1 ? 'Punkt' : 'Punkte'}
                </span>
              )}

              {a.wvZustand && (
                <span className={`detail-chip wv-${a.wvZustand}`} title={a.wvHerkunft}>
                  <span className="detail-chip-punkt" />
                  {a.wvText}
                </span>
              )}

              <div className="detail-statusaktionen">
                {/*
                  Die Abnahme bleibt ein eigener Knopf, und zwar grün: Sie
                  schließt ab. Alles davor – bestellen, AB, Produktion – steht
                  in derselben Auswahl wie sonst auch; es wäre eine zweite
                  Bedienung für dieselbe Handlung, sie eigens zu bauen.

                  Nachfassen, Rückmeldung und Erinnerungsmail entfallen hier.
                  Sie gehören zur Angebotsphase; ein erteilter Auftrag liegt
                  nicht mehr beim Kunden zur Entscheidung.
                */}
                {/*
                  Drei Zustände, kein blassgrüner (Richtlinie 17): gesperrt ist
                  weiß-grau mit dem Satz daneben, möglich ist grün gefüllt,
                  erledigt verschwindet der Knopf.
                */}
                {inAbwicklung && !a.abnahmeMoeglich && (
                  <span className="detail-abnahme-hinweis">Wird aktiv, sobald alle Bestellungen montiert sind.</span>
                )}
                {inAbwicklung && (
                  <button
                    type="button"
                    className="detail-abnahme"
                    disabled={!a.abnahmeMoeglich}
                    title={
                      a.abnahmeMoeglich
                        ? 'Alle Bestellungen sind montiert – der Auftrag kann abgenommen werden'
                        : (() => {
                            const montiert = meta.abwicklung.schritte.findIndex((s) => s.key === 'montiert');
                            const n = a.bestellungen.filter((b) => b.schrittIndex < montiert).length;
                            return `${n} ${n === 1 ? 'Bestellung ist' : 'Bestellungen sind'} noch nicht montiert`;
                          })()
                    }
                    onClick={() => setAbnahmeOffen(true)}
                  >
                    Auftrag abgenommen
                  </button>
                )}

                {istAuftrag && a.status === 'Abgenommen' && (
                  <span className="detail-abgeschlossen">
                    Abgenommen am {a.statusDatumText} durch {a.statusUser}
                  </span>
                )}

                {/*
                  In der Abwicklung gibt es keinen „Status ändern"-Knopf
                  (Richtlinie 4): Der Stand ist abgeleitet, quittiert wird auf
                  der Bestellkarte. Der Weg zurück – ein geplatzter Auftrag –
                  liegt im ⋯-Menü.
                */}
                {!istAuftrag && (
                <label className="detail-statuswahl">
                  <span>Status ändern</span>
                  <span className="detail-statuswahl-pfeil">▾</span>
                  <select
                    value=""
                    disabled={statusMutation.isPending}
                    onChange={(e) => {
                      statusWaehlen(e.target.value);
                      e.target.value = '';
                    }}
                  >
                    <option value="">wählen …</option>
                    {/*
                      Nur was der Server zulässt. „Abgenommen" etwa erst ab
                      „Auftrag erteilt" – ein Angebot kann nicht abgenommen
                      werden. Die Liste kommt vom Server, damit Maske und
                      Prüfung nicht auseinanderlaufen.
                    */}
                    {/*
                      Gesperrte Ziele werden gezeigt, nicht versteckt: Wer sie
                      sucht, soll sehen, dass es sie gibt und warum sie gerade
                      nicht gehen. Ein verschwundener Eintrag wirkt wie ein
                      Fehler, ein gesperrter erklärt sich.
                    */}
                    {/*
                      Nur, was jetzt möglich ist – in der Reihenfolge des
                      Ablaufs. Wo man steht und was danach kommt, zeigt die
                      Zeitleiste darunter; die Auswahl soll keine Fehlgriffe
                      anbieten. Bestellt bis In Produktion setzt niemand von
                      Hand: Sie folgen den Bestellungen im Block „Abwicklung".
                    */}
                    {statusReihe
                      .filter(
                        (x) =>
                          x !== a.status &&
                          a.statusZiele.includes(x) &&
                          !(istAuftrag && meta.abwicklungStatus.includes(x)),
                      )
                      .map((x) => {
                        const pruefSperre =
                          x !== 'Gesendet' && pruefpflichtig(x) && a.versand.pruefungOffen;
                        return (
                          <option key={x} value={x} disabled={pruefSperre}>
                            {x}
                            {pruefSperre ? '  🔒' : ''}
                          </option>
                        );
                      })}
                  </select>
                </label>
                )}

                {/*
                  Nur der gesperrte Fall bekommt einen Satz.
                  „Die Ablage wird nachgeführt" stand vorher immer da und
                  sagte nichts, was jemand wissen muss – es ist der
                  Normalfall. Ein Hinweis, der bei jedem Vorgang gleich lautet,
                  wird nach dem dritten Mal nicht mehr gelesen und kostet nur Platz.
                */}

                {a.versand.bereit && (a.versand.pruefungOffen || a.versand.ankuendigungOffen) && (
                  <div className="detail-statusfuss detail-statusfuss--gesperrt">
                    „Gesendet" ist erst möglich, wenn{' '}
                    {[
                      a.versand.pruefungOffen ? 'die Prüfung' : '',
                      a.versand.ankuendigungOffen ? 'der Ankündigungsanruf' : '',
                    ]
                      .filter(Boolean)
                      .join(' und ')}{' '}
                    {a.versand.pruefungOffen && a.versand.ankuendigungOffen ? 'erledigt sind' : 'erledigt ist'} –
                    oder mit Begründung übersprungen, siehe „Vor dem Versand".
                  </div>
                )}

                {/*
                  Vor dem Versand ruft man an, um das Angebot anzukündigen –
                  danach, um die Rückmeldung zu holen. Zwei Fragen, zwei
                  Dialoge, dieselbe Chronik.
                */}
                {['START', 'in Arbeit', 'Freigabe Technik'].includes(a.status) && (
                  <button
                    type="button"
                    className="detail-zweitknopf"
                    onClick={() => setAnkuendigungOffen(true)}
                  >
                    <IconTelefon groesse={15} />
                    Ankündigung erfassen
                  </button>
                )}

                {!istAuftrag && (
                  <button
                    type="button"
                    className="detail-zweitknopf"
                    onClick={() => setAnrufOffen(true)}
                  >
                    <IconTelefon groesse={15} />
                    Rückmeldung erfassen
                  </button>
                )}

                <div className="detail-mehr-huelle">
                  <button
                    type="button"
                    className="detail-mehr"
                    title="Weitere Aktionen"
                    aria-expanded={mehrOffen}
                    onClick={() => setMehrOffen((o) => !o)}
                  >
                    ⋯
                  </button>
                  {mehrOffen && (
                    <div className="detail-mehr-menue">
                      {a.ergaenzbareGewerke.length > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setMehrOffen(false);
                            setGewerkOffen(true);
                          }}
                        >
                          Gewerk ergänzen
                        </button>
                      )}
                      {/* Erinnerungsmails gibt es nur in der Angebotsphase. */}
                      {!istAuftrag && (
                        <button
                          type="button"
                          onClick={() => {
                            setMehrOffen(false);
                            setMailOffen(true);
                          }}
                        >
                          Erinnerungsmail ansehen
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={syncMutation.isPending}
                        onClick={() => {
                          setMehrOffen(false);
                          syncMutation.mutate();
                        }}
                      >
                        Abgleich erneut ausführen
                      </button>
                      {/*
                        Der Weg aus dem Auftrag heraus – wenn er platzt. Selten,
                        deshalb hier und nicht als Auswahl im Kopf.
                      */}
                      {istAuftrag &&
                        a.statusZiele
                          .filter((x) => !meta.auftragsStatus.includes(x) && x !== a.status)
                          .map((x) => (
                            <button
                              key={x}
                              type="button"
                              className="detail-mehr-leise"
                              onClick={() => {
                                setMehrOffen(false);
                                statusWaehlen(x);
                              }}
                            >
                              Status auf „{x}" setzen
                            </button>
                          ))}
                      {nutzer?.istAdmin && (
                        <button
                          type="button"
                          disabled={verknuepfungMutation.isPending}
                          title="Sucht den Vorgangsordner anhand der Nummer neu"
                          onClick={() => {
                            setMehrOffen(false);
                            verknuepfungMutation.mutate();
                          }}
                        >
                          {verknuepfungMutation.isPending
                            ? 'Sucht …'
                            : 'Ablage neu suchen'}
                        </button>
                      )}
                      {/*
                        Nur am selben Tag und nur für Wechsel aus Kora – der
                        Server prüft beides noch einmal.
                      */}
                      {a.verlauf.length >= 2 &&
                        a.verlauf[a.verlauf.length - 1]!.user !== BESTANDSQUELLE &&
                        a.verlauf[a.verlauf.length - 1]!.datum === meta.heute && (
                          <button
                            type="button"
                            className="detail-mehr-leise"
                            title="Nur im Fehlerfall: der letzte Statuswechsel wird entfernt"
                            onClick={() => {
                              setMehrOffen(false);
                              setZurueckOffen2(true);
                            }}
                          >
                            Verklickt? Letzten Statuswechsel zurücknehmen
                          </button>
                        )}
                      {nutzer?.istAdmin && (
                        <button
                          type="button"
                          className="detail-mehr-gefahr"
                          onClick={() => {
                            setMehrOffen(false);
                            setLoeschAbfrage(true);
                          }}
                        >
                          Angebot löschen
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ---- Zeitachse: verortet den Status, wiederholt ihn nicht ---- */}
            <div className="detail-prozess">
              {/*
                Ein Verlauf, nicht drei (Richtlinie 1): die Hauptader als graue
                Zeile im selben Raster wie der Teilprozess, darunter je
                Bestellung eine Zeile unter denselben zehn Spalten. So sieht man
                ohne Lesen, dass Bestellung 2 zwei Schritte weiter ist als
                Bestellung 1 – und der am weitesten links stehende rote Knoten
                ist der Auftragsstand.
              */}
              <div className="detail-abschnitt-kopf">
                <div className="detail-abschnitt-titel">Verlauf im Prozess</div>
                {auftragsphase && a.bestellungen.length > 0 && (
                  <span className="detail-abschnitt-hinweis">
                    eine Zeile je Bestellung · der Auftrag folgt der hintersten
                  </span>
                )}
              </div>
              <div className={`pr ${auftragsphase ? 'pr--auftrag' : ''}`}>
                <div className="pr-zeile pr-hauptader">
                  <div className="pr-links">
                    <div className="pr-links-titel">Angebot → Auftrag</div>
                    <div className="pr-links-unter">
                      {a.status === 'Abgenommen' ? 'abgenommen' : auftragsphase ? 'abgeschlossen' : 'läuft'}
                    </div>
                  </div>
                  <div className="pr-knoten pr-knoten--6">
                    {stationen.map(({ status: st }, i) => {
                      /*
                       * Was hinter der aktuellen Station liegt, ist offen – auch
                       * wenn es früher einmal erreicht war. Ein Angebot, das nach
                       * „Gesendet" zurück auf START ging, beginnt von vorn.
                       */
                      const wann = aktuellIndex >= 0 && i > aktuellIndex ? undefined : erreicht.get(st);
                      const jetzt = st === anzeigeStatus;
                      const entfallen = !jetzt && !wann && aktuellIndex > i;
                      const zustand = jetzt ? 'jetzt' : wann ? 'erreicht' : entfallen ? 'entfallen' : 'offen';
                      const gruen = st === 'Abgenommen' && a.status === 'Abgenommen';
                      return (
                        <div key={st} className={`pr-zelle ${zustand} ${gruen ? 'gruen' : ''}`}>
                          <div className="pr-spur">
                            <span className={`pr-linie ${i === 0 ? 'unsichtbar' : ''} ${i <= aktuellIndex ? 'dunkel' : ''}`} />
                            <span className="pr-punkt" />
                            <span className={`pr-linie ${i === stationen.length - 1 ? 'unsichtbar' : ''} ${i < aktuellIndex ? 'dunkel' : ''}`} />
                          </div>
                          <div className="pr-name">{st}</div>
                          <div className="pr-unter">
                            {entfallen ? (
                              'entfallen'
                            ) : jetzt && auftragsphase && st === 'Auftrag erteilt' ? (
                              [wann?.datumText, a.abwicklungStand.split(' · ')[0]].filter(Boolean).join(' · ')
                            ) : jetzt && st === 'Abgenommen' ? (
                              [wann?.datumText, wann?.user].filter(Boolean).join(' · ')
                            ) : jetzt ? (
                              <>
                                {a.alterText} im Status
                                {st === 'Gesendet' && a.versender ? ` · versendet von ${a.versender}` : ''}
                              </>
                            ) : wann && st === 'Gesendet' ? (
                              <>
                                {[wann.datumText, wann.user].filter(Boolean).join(' · ')}
                                {/*
                                  Das Datum stammt aus dem übernommenen Bestand und
                                  ist zu jung, wenn der Status nachträglich gesetzt
                                  wurde. Die
                                  Korrektur gehört dorthin, wo die falsche Zahl
                                  auffällt.
                                */}
                                <button
                                  type="button"
                                  className="detail-datum-knopf"
                                  title="Versanddatum korrigieren"
                                  onClick={() => setVersandKorrektur((o) => !o)}
                                >
                                  korrigieren
                                </button>
                              </>
                            ) : wann ? (
                              [wann.datumText, wann.user].filter(Boolean).join(' · ')
                            ) : st === 'Abgenommen' ? (
                              'offen'
                            ) : (
                              ''
                            )}
                            {a.serviceOffen > 0 &&
                              st === (a.status === 'Abgenommen' ? 'Abgenommen' : 'Auftrag erteilt') && (
                                <div className="detail-service-marke">⤷ Service läuft</div>
                              )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {auftragsphase && a.bestellungen.length > 0 && (
                  <>
                    <div className="pr-zeile pr-kopfzeile">
                      <div className="pr-links">
                        <div className="pr-links-titel">Abwicklung</div>
                        <div className="pr-links-unter">je Bestellung</div>
                      </div>
                      <div className="pr-knoten pr-knoten--10">
                        {meta.abwicklung.schritte.map((s) => (
                          <div key={s.key} className="pr-spaltenkopf">
                            {s.lang}
                          </div>
                        ))}
                      </div>
                    </div>
                    {a.bestellungen.map((b) => (
                      <div key={b.id} className="pr-zeile pr-bestellung">
                        <div className="pr-links pr-links--bestellung">
                          <span className="pr-quadrat">{b.nr}</span>
                          <div className="pr-links-text">
                            <div className="pr-links-titel">{b.lieferant || `Bestellung ${b.nr}`}</div>
                            <div className="pr-links-unter">
                              {b.schrittLang} · {b.tageImSchritt} T.
                            </div>
                          </div>
                        </div>
                        <div className="pr-knoten pr-knoten--10">
                          {meta.abwicklung.schritte.map((s, i) => {
                            const z = i < b.schrittIndex ? 'erledigt' : i === b.schrittIndex ? 'aktuell' : 'offen';
                            return (
                              <div
                                key={s.key}
                                className={`pr-zelle ${z} ${b.schritt === 'fertig' && z === 'aktuell' ? 'gruen' : ''}`}
                                title={`${s.lang}${z === 'aktuell' ? ` · seit ${b.tageImSchritt} Tagen` : ''}`}
                              >
                                <div className="pr-spur">
                                  <span className={`pr-linie ${i === 0 ? 'unsichtbar' : ''} ${i <= b.schrittIndex ? 'dunkel' : ''}`} />
                                  <span className="pr-punkt" />
                                  <span className={`pr-linie ${i === meta.abwicklung.schritte.length - 1 ? 'unsichtbar' : ''} ${i < b.schrittIndex ? 'dunkel' : ''}`} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>

              {versandKorrektur && (
                <div className="detail-datum-feld">
                  <label>
                    <span>Tatsächliches Versanddatum</span>
                    <input
                      type="date"
                      max={meta.heute}
                      defaultValue={a.versandDatum}
                      disabled={versanddatumMutation.isPending}
                      onChange={(e) => {
                        if (e.target.value) versanddatumMutation.mutate(e.target.value);
                      }}
                    />
                  </label>
                  <span className="detail-datum-hinweis">
                    Kora übernimmt das Datum aus dem Protokoll des Vorgangs. Wurde der Status
                    nachträglich gesetzt, ist es zu jung – dann steht hier das richtige.
                  </span>
                  <button
                    type="button"
                    className="detail-datum-knopf"
                    onClick={() => setVersandKorrektur(false)}
                  >
                    schließen
                  </button>
                </div>
              )}
            </div>

            {/*
              ---- Angebotsprüfung ----
              Zwischen Verlauf und letzter Aktivität: Sie erklärt, warum der
              Status gerade nicht weitergeht. Bei Aufträgen entfällt sie – dort
              ist die Prüfung längst vorbei.
            */}
            {/*
              Ist die Prüfung abgeschaltet, ist sie nicht da – nicht ausgegraut
              und nicht als leerer Kasten. Ein Block, der nur sagt „hier wäre
              etwas, wenn es eingerichtet wäre", kostet jeden Leser Zeit.
            */}
            {/*
              ---- Vor dem Versand ----
              Prüfung und Ankündigung, beide Pflicht, beide mit Begründung
              überspringbar. Nur solange das Angebot noch nicht draußen ist.
            */}
            {a.versand.bereit && !istAuftrag && (
              <Versandschritte
                angebot={a}
                onPruefen={() =>
                  document.querySelector('.pb')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }
                onAnkuendigen={() => setAnkuendigungOffen(true)}
                onFertig={aktualisieren}
              />
            )}

            {/*
              ---- Abwicklung ----
              Die Bestellungen des Auftrags: hier werden Schritte quittiert.
              Der Angebotsstatus folgt ihnen, die Abwicklungsliste zeigt sie.
            */}
            {istAuftrag && <Abwicklungsblock angebot={a} onFertig={aktualisieren} />}

            {!istAuftrag && meta.pruefungAktiv && (
              <Pruefblock angebot={a} angesprungen={oeffne === 'pruefung'} />
            )}

            {/* ---- Letzte Aktivität, Chronik dahinter ---- */}
            <div className="detail-aktivitaet">
              <div className="detail-aktivitaet-links">
                <span className="detail-aktivitaet-symbol">
                  {letzteAktivitaet?.art === 'rueckmeldung' ? (
                    <IconTelefon groesse={15} />
                  ) : letzteAktivitaet?.art === 'mail' ? (
                    <IconMail groesse={15} />
                  ) : (
                    <IconGesendet groesse={15} />
                  )}
                </span>
                <div className="detail-aktivitaet-text">
                  <div className="detail-abschnitt-titel">Letzte Aktivität</div>
                  <div className="detail-aktivitaet-zeile">
                    {letzteAktivitaet
                      ? `${letzteAktivitaet.titel} · ${letzteAktivitaet.zeitText}`
                      : 'Noch nichts geschehen.'}
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="detail-chronik-knopf"
                onClick={() => setChronikOffen((o) => !o)}
              >
                Chronik {chronikOffen ? 'schließen' : 'öffnen'} ({chronik.length})
                <span className="detail-quelle-pfeil">{chronikOffen ? '▴' : '▾'}</span>
              </button>
            </div>

            {chronikOffen && (
              <div className="detail-chronik">
                <div className="detail-chronik-filter">
                  {(
                    [
                      ['alle', 'Alles'],
                      ['status', 'Status'],
                      ['pruefung', 'Prüfungen'],
                      ['aenderung', 'Änderungen'],
                      ['mail', 'Mails'],
                      ['rueckmeldung', 'Rückmeldungen'],
                    ] as const
                  ).map(([wert, label]) => (
                    <button
                      key={wert}
                      type="button"
                      className={`detail-filter-chip ${chronikFilter === wert ? 'aktiv' : ''}`}
                      onClick={() => setChronikFilter(wert)}
                    >
                      {label}
                      <span className="detail-filter-zahl">{chronikZahl[wert]}</span>
                    </button>
                  ))}
                </div>

                {/*
                  Eine Spur mit Symbolen statt einer Liste: Die Art des
                  Ereignisses ist auf einen Blick zu sehen, ohne sie in jeden
                  Titel zu schreiben. Die Linie hält die Einträge zusammen.
                */}
                <div className="detail-chronik-liste">
                  {chronikGefiltert.map((c, i) => (
                    <div key={c.id} className={`detail-chronik-eintrag art-${c.art}`}>
                      <div className="detail-chronik-spur">
                        <span className="detail-chronik-symbol">
                          {c.anlage ? (
                            '★'
                          ) : c.art === 'aenderung' ? (
                            <IconStift groesse={14} />
                          ) : c.art === 'rueckmeldung' ? (
                            <IconTelefon groesse={14} />
                          ) : c.art === 'mail' ? (
                            <IconMail groesse={14} />
                          ) : c.art === 'pruefung' ? (
                            '⌕'
                          ) : (
                            '→'
                          )}
                        </span>
                        {i < chronikGefiltert.length - 1 && <span className="detail-chronik-linie" />}
                      </div>

                      <div className="detail-chronik-inhalt">
                        <div className="detail-chronik-kopf">
                          <span className="detail-chronik-titel">{c.titel}</span>
                          <span className="detail-chronik-zeit">{c.zeitText}</span>
                        </div>
                        <div className="detail-chronik-unter">{c.unter}</div>
                        {c.notiz && <div className="detail-chronik-notiz">{c.notiz}</div>}

                        {/*
                          Bei einer Mail führt die Chronik zum Wortlaut. Die
                          1. Erinnerung geht ohne Freigabe hinaus – was der
                          Kunde bekommen hat, muss nachlesbar sein.
                        */}
                        {c.mailId !== undefined && (
                          <button
                            type="button"
                            className="detail-chronik-mail"
                            onClick={() => {
                              const m = a.erinnerungen.find((e) => e.id === c.mailId);
                              if (m) setMailAnsicht(m);
                            }}
                          >
                            Wortlaut ansehen
                          </button>
                        )}

                        {/*
                          Der Prüfbericht ist der einzige Ort, an dem die
                          Freigaben stehen. Ein Abhaken erzeugt bewusst keinen
                          eigenen Chronikeintrag – bei fünf offenen Punkten wäre
                          die Chronik danach unlesbar.
                        */}
                        {c.pruefung && <Pruefbericht lauf={c.pruefung} />}
                      </div>
                    </div>
                  ))}
                  {chronikGefiltert.length === 0 && (
                    <div className="detail-leer">Nichts in dieser Auswahl.</div>
                  )}
                </div>
              </div>
            )}
            {loeschAbfrage && (
              <div className="detail-loeschfeld">
                <div className="detail-loeschtext">
                  Löscht {a.name} samt Verlauf, Rückmeldungen und Erinnerungen aus Kora. Der
                  Vorgangsordner bleibt bestehen und muss von Hand entfernt werden. Der Schritt
                  lässt sich nicht rückgängig machen.
                </div>
                <div className="detail-loeschknoepfe">
                  <button
                    type="button"
                    className="detail-loeschen-ja"
                    disabled={loeschMutation.isPending}
                    onClick={() => loeschMutation.mutate()}
                  >
                    {loeschMutation.isPending ? 'Löscht …' : 'Endgültig löschen'}
                  </button>
                  <button type="button" onClick={() => setLoeschAbfrage(false)}>
                    Abbrechen
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ---- Rechte Spalte: der Kunde und die Zahlen ---- */}
        <div className="detail-seitenspalte">
          <div className="detail-karte">
            {/*
              Zugeklappt (TEAM-CHAT.md, 3.1): Avatar, Name, eine Zeile.
              Aufgeklappt kommen Kontakt, die weiteren Vorgänge und
              „Stammdaten bearbeiten" dazu. Vorgabe ist zu – der Platz
              gehört dem Kommentarfaden, und wer den Kunden anrufen will,
              klappt einmal auf.
            */}
            <button
              type="button"
              className="detail-kundenkopf"
              aria-expanded={kundeAufgeklappt}
              title={kundeAufgeklappt ? 'Kundendaten zuklappen' : 'Kontakt und weitere Vorgänge anzeigen'}
              onClick={() => setKundeAufgeklappt((o) => !o)}
            >
              <span className="detail-kundenavatar gross">{a.kunde.initialen}</span>
              <span className="detail-kundenkopf-text">
                <span className="detail-kundenname">{a.kunde.name}</span>
                <span className="detail-kundenmeta">
                  {a.kunde.typ === 'organisation' ? 'Organisation' : 'Privatkunde'}
                  {a.kunde.ort ? ` · ${a.kunde.ort}` : ''}
                  {geschwister.length > 0
                    ? ` · ${geschwister.length} weitere${geschwister.length === 1 ? 'r Vorgang' : ' Vorgänge'}`
                    : ''}
                </span>
              </span>
              <span className="detail-kundenpfeil">{kundeAufgeklappt ? '▴' : '▾'}</span>
            </button>


            {/*
              Kontakt als schlichte Textlinks: Sie führen aus Kora hinaus und
              dürfen nicht mit dem Statuswechsel konkurrieren.

              Wohin der Klick führt, hängt von der Nummer ab. Eine Mobilnummer
              öffnet WhatsApp – so wird bei uns nachgefasst. Bei einem
              Festnetzanschluss wäre das eine Sackgasse, dort wählt der Klick.
              Die Mail geht über „mailto:" an das Standardprogramm, also an
              Outlook.
            */}
            {kundeAufgeklappt && (
              <>
            <div className="detail-kontakt">
              {[
                { wert: a.kunde.mobil, was: 'Mobil' },
                { wert: a.kunde.mobilGeschaeftlich, was: 'Mobil geschäftlich' },
                { wert: a.kunde.telefon, was: 'Telefon' },
              ]
                .filter((n) => n.wert)
                .map((n) => {
                  const mobil = istMobil(n.wert);
                  return (
                    <a
                      key={n.was}
                      className="detail-kontaktlink"
                      href={mobil ? `https://wa.me/${whatsappNummer(n.wert)}` : `tel:${whatsappNummer(n.wert)}`}
                      target={mobil ? '_blank' : undefined}
                      rel={mobil ? 'noreferrer' : undefined}
                      title={mobil ? `${n.was} – öffnet WhatsApp` : `${n.was} – wählt die Nummer`}
                    >
                      <IconTelefon groesse={14} />
                      {n.wert}
                      {mobil && <span className="detail-kontakt-zusatz">WhatsApp</span>}
                    </a>
                  );
                })}

              {a.kunde.mail && (
                <a
                  className="detail-kontaktlink"
                  href={`mailto:${a.kunde.mail}`}
                  title="Öffnet Outlook mit einer neuen Nachricht"
                >
                  <IconMail groesse={14} />
                  {a.kunde.mail}
                </a>
              )}

              {!a.kunde.mail && !a.kunde.telefon && !a.kunde.mobil && !a.kunde.mobilGeschaeftlich && (
                <div className="detail-leer">Keine Kontaktdaten hinterlegt.</div>
              )}
            </div>

                {geschwister.length > 0 && (
                  <div className="detail-weitere-vorgaenge">
                    <span className="detail-datenlabel">Weitere Vorgänge</span>
                    {geschwister.map((g) => (
                      <button
                        key={g.id}
                        type="button"
                        className="detail-vorgangslink"
                        onClick={() => navigate(`/angebot/${g.id}`, { state: { herkunft: herkunft.key } })}
                      >
                        {g.name} · {g.status}, {g.alterText}
                      </button>
                    ))}
                  </div>
                )}

                <button type="button" className="detail-stammdaten" onClick={() => setKundeOffen(true)}>
                  Stammdaten bearbeiten
                </button>
              </>
            )}

            {/*
              Personen werden benannt, nicht erklärt. Wer die Rolle innehat ist
              die Information – woher der Wert stammt, interessiert niemanden.
            */}
            {/*
              Bei wem der Vorgang gerade liegt (Mein Tag v6): die Person hinter der
              Rolle laut Zuständigkeitsmatrix – oder die, der er übergeben wurde.
              Klick öffnet die Übergabe mit Begründung. Bei Aufträgen steht das je
              Bestellung auf der Karte, hier nur der Service.
            */}
            {(a.liegtBei || a.serviceOffen > 0) && (
              <div className="detail-liegtbei">
                <span className="detail-datenlabel">Liegt bei</span>
                <LiegtBei
                  lb={a.liegtBei ?? a.serviceLiegtBei}
                  onUebergeben={() => setUebergabe(a.liegtBei ?? a.serviceLiegtBei)}
                />
                <span className="detail-liegtbei-schritt">{(a.liegtBei ?? a.serviceLiegtBei).schrittLabel}</span>
              </div>
            )}

            {/*
              Kalkulant und Versender stehen seit TEAM-CHAT.md 3.1 in der
              Kopfzeile. Hier standen sie drei Bildschirmhöhen entfernt von
              der Entscheidung, die sie betreffen.
            */}

            {/* Nachgefasst wird ein Angebot, kein Auftrag. */}
            {!istAuftrag && (
              <div className="detail-datenzeile">
                <span className="detail-datenlabel">Nachfassen</span>
                <span className={`detail-datenwert ton-${nachfassTon}`} title={a.wvHerkunft}>
                  {nachfassText}
                </span>
              </div>
            )}

            <div className="detail-datenzeile">
              <span className="detail-datenlabel">Geprüft</span>
              <span
                className={`detail-datenwert ton-${
                  !a.pruefung.moeglich
                    ? 'grau'
                    : a.pruefung.abgeschlossen
                      ? 'gruen'
                      : 'bernstein'
                }`}
              >
                {a.pruefung.geprueftAm
                  ? `${a.pruefung.geprueftAmText} · ${a.pruefung.geprueftVon}`
                  : 'noch nicht geprüft'}
              </span>
            </div>

            <div className="detail-datenzeile">
              <span className="detail-datenlabel">Angelegt</span>
              <span className="detail-datenwert">{dat(a.angelegtAm)}</span>
            </div>

            <div className="detail-datenzeile">
              <span className="detail-datenlabel">Auftragshöhe</span>
              <Betragsfeld
                wert={a.auftrag}
                laeuft={wertMutation.isPending}
                onSpeichern={(neu) => wertMutation.mutate(neu)}
              />
            </div>

            <div className="detail-hinweis">
              {a.status === 'Auftrag erteilt' || a.status === 'Abgenommen'
                ? 'Verbindlich – fließt in die Auswertung.'
                : 'Vorgemerkt. Fließt erst mit „Auftrag erteilt" in die Prognose.'}
            </div>
          </div>

          {/*
            Der Kommentarfaden steht dauerhaft in der rechten Spalte
            (TEAM-CHAT.md, 3) – nicht hinter einem Reiter. Ein Faden, den
            man erst aufklappen muss, wird nicht gelesen, und dann schreibt
            wieder jeder woanders.
          */}
          <Kommentarfaden
            angebotId={a.id}
            art={auftragsphase ? 'Auftrag' : 'Angebot'}
            ordner={`${auftragsphase ? 'Aufträge' : 'Angebote'}/${a.ordnerName || a.name}`}
          />
        </div>
      </div>

      {gewerkOffen && (
        <GewerkModal
          angebot={a}
          onSchliessen={() => setGewerkOffen(false)}
          onFertig={(neueId) => {
            aktualisieren();
            navigate(`/angebot/${neueId}`);
          }}
        />
      )}
      {versendetOffen && (
        <VersendetModal
          angebot={a}
          onSchliessen={() => setVersendetOffen(false)}
          onFertig={aktualisieren}
        />
      )}
      {auftragOffen && (
        <AuftragModal
          angebot={a}
          onSchliessen={() => setAuftragOffen(false)}
          onFertig={aktualisieren}
        />
      )}
      {uebergabe && (
        <ZustaendigModal
          angebot={a}
          liegtBei={uebergabe}
          onSchliessen={() => setUebergabe(null)}
          onFertig={() => {
            setUebergabe(null);
            aktualisieren();
          }}
        />
      )}

      {anrufOffen && (
        <AnrufModal angebot={a} onSchliessen={() => setAnrufOffen(false)} onFertig={aktualisieren} />
      )}
      {versandWarnung && (
        <Modal
          titel="Versand noch nicht möglich"
          untertitel={a.name}
          breite={440}
          onSchliessen={() => setVersandWarnung(false)}
          fuss={
            <>
              <KnopfSekundaer
                onClick={() => {
                  setVersandWarnung(false);
                  document.querySelector('.vs')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
              >
                Zu den Schritten
              </KnopfSekundaer>
              <KnopfPrimaer onClick={() => setVersandWarnung(false)}>Verstanden</KnopfPrimaer>
            </>
          }
        >
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.55 }}>
            Angebot zuerst ankündigen bzw. prüfen, erst dann ist ein Versand möglich.
          </p>
          <div className="modal-hinweis">
            Der Status bleibt unverändert. Was noch fehlt, steht im Block „Vor dem Versand" – dort
            lässt sich jeder Schritt erledigen oder mit Begründung überspringen.
          </div>
        </Modal>
      )}
      {zurueckOffen2 && (
        <Modal
          titel="Letzten Statuswechsel zurücknehmen"
          untertitel={a.name}
          breite={460}
          fest={statusZurueck.isPending}
          onSchliessen={() => setZurueckOffen2(false)}
          fuss={
            <>
              <KnopfSekundaer onClick={() => setZurueckOffen2(false)} disabled={statusZurueck.isPending}>
                Doch nicht
              </KnopfSekundaer>
              <KnopfPrimaer onClick={() => statusZurueck.mutate()} disabled={statusZurueck.isPending}>
                {statusZurueck.isPending ? 'Wird zurückgenommen …' : 'Zurücknehmen'}
              </KnopfPrimaer>
            </>
          }
        >
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.55 }}>
            „{a.status}" wird entfernt; der Vorgang steht danach wieder auf „
            {a.verlauf[a.verlauf.length - 2]?.status}".
          </p>
          <div className="modal-hinweis">
            Nur für Verklicker. Was der Wechsel sonst ausgelöst hat – Ordner verschoben, Beträge
            gesetzt – bleibt stehen. Die Rücknahme steht in der Chronik.
          </div>
        </Modal>
      )}
      {ankuendigungOffen && (
        <AnkuendigungModal
          angebot={a}
          onSchliessen={() => setAnkuendigungOffen(false)}
          onFertig={aktualisieren}
        />
      )}
      {mailOffen && <MailVorschauModal angebot={a} onSchliessen={() => setMailOffen(false)} />}
      {mailAnsicht && (
        <MailAnsichtModal
          mail={mailAnsicht}
          angebot={a}
          onSchliessen={() => setMailAnsicht(null)}
        />
      )}

      {zurueckOffen && (
        <ZurueckModal
          angebot={a}
          onSchliessen={() => setZurueckOffen(false)}
          onFertig={aktualisieren}
        />
      )}

      {abnahmeOffen && (
        <AbnahmeModal
          angebot={a}
          onSchliessen={() => setAbnahmeOffen(false)}
          onFertig={aktualisieren}
        />
      )}

      {kundeOffen && (
        <KundenModal
          kundeId={a.kunde.id}
          onSchliessen={() => setKundeOffen(false)}
          onGespeichert={() => {
            setKundeOffen(false);
            aktualisieren();
          }}
        />
      )}
    </div>
  );
}